import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { datesService } from '../../services/DatesService';
import Button from '@showclix-shared/Button';
import { BackgroundImage, ListingListItemImage } from '@showclix-shared/ListingComponents';
import { AppContext } from '../../App';
import { EVENT_SALE_PAST, EVENT_SALE_FUTURE } from '../../constants/onsaleStatus';
import Toast from "@showclix-shared/Toast"
import { useAnalytics, useBaseEventData } from '@/contexts/Analytics';
import _omit from 'lodash/omit';

const HERO_LAYOUT = 'hero';

const ListingListItem = props => {
  const {
    t,
    serverTimezoneName,
    embeddedCheckout,
    loadingListingId,
    setLoadingListingId,
    embedCaughtRenderingError,
    hideStartingPrices,
  } = useContext(AppContext);
  const {
    getListingTime,
    isLaterThanNow,
    serverDateToBrowserDate,
    getFormattedEventStart,
  } = datesService();

  const isNotOnSale = listing => listing.sold_out || listing.onsale_status === EVENT_SALE_PAST || listing.onsale_status === EVENT_SALE_FUTURE || !listing.has_active_price_levels;
  const baseEventData = useBaseEventData();

  const { reportEvent, reportGTM } = useAnalytics();

  const getImage = listing => {
    if (listing.image && listing.image !== '') {
      const containerStyle = { backgroundImage: '' };
      if (listing?.layout === HERO_LAYOUT && !listing?.background_image) {
        containerStyle.backgroundImage = `url("${listing?.background_image}")`;
      }

      return (<ListingListItemImage
        src={listing.image}
        alt={listing.listing_title}
        style={containerStyle}
        className={listing?.layout === HERO_LAYOUT && listing.background_image ? 'listing-item__hero__container--has-bg-img' : ''}
      />);
    } else {
      return (
        <div className="listing-item__image listing-item__image--empty">
          <i className="icon-calendar" aria-hidden="true" />
        </div>
      );
    }
  };

  const getCheckoutLink = listing => '/tickets/' + listing.listing_slug;

  const getBuyTicketsLink = listing => getCheckoutLink(listing);

  const getDate = listing => {
    if (listing.custom_time) {
      return listing.custom_time;
    }

    if (parseInt(listing.children_count) > 0 && !listing.single_event_series) {
      return t('org_listing__multiple_dates_and_times');
    }

    if (listing.multipleTimes) {
      return 'Multiple Times';
    }

    return (<>
      <span>{getFormattedEventStart(listing.event_start)}</span><br />
      <span>{getListingTime(listing.event_start)}{listing.event_end ? ` - ${getListingTime(listing.event_end)}` : ''}</span>
    </>);
  };

  const getVenueInfo = listing => {
    if (listing.custom_venue) {
      return listing.custom_venue;
    }
    return `${listing.venue_name}, ${listing.venue_city}`;
  };

  const getFooterContent = listing => {
    if (isNotOnSale(listing)) {
      if (embeddedCheckout) {
        return (
          <p className='text-md-regular mb-0'>
            <Button
              className="listing-item__details-button full-width"
              variant={Button.variant.link}
              onClick={loadDynamicListing}
              disabled={loadingListingId === listing.listing_id || embedCaughtRenderingError}
            >
              {t('org_listing__event_details')}
            </Button>
          </p>
        );
      }

      return (
        <p className='text-md-regular mb-0'>
          <a
            className="button button--link full-width listing-item__details-button"
            href={getBuyTicketsLink(props.listing)}
            target="_blank"
          >
            {t('org_listing__event_details')}
          </a>
        </p>
      );
    }

    if(hideStartingPrices) {
      return null;
    }

    if (listing.has_only_free_tickets) {
      return (<p className='text-md-regular mb-0'>{t('org_listing__free_entry')}</p>);
    }

    if (!isNaN(listing.min_price) && listing.min_price_fmt) {
      return (<p className='text-md-regular mb-0'>{t('org_listing__start_at') + ' ' + listing.min_price_fmt}</p>);
    }

    return null;
  };

  const reportOpenListing = () => {
    reportEvent('google-analytics-4')('event', 'orglisting_event_selected', {
      ..._omit(baseEventData, ['event_ids', 'event_names']),
      series: props.listing.is_series,
      reserved: props.listing.is_seated,
    });
    reportGTM({
      event: 'orglisting_event_selected',
      ..._omit(baseEventData, ['event_ids', 'event_names']),
      series: props.listing.is_series,
      reserved: props.listing.is_seated,
    });
  }

  const loadDynamicListing = async () => {
    if (!loadingListingId) {
      setLoadingListingId(props.listing.listing_id);
      const loadListingResult = await embeddedCheckout.openCheckoutToListing(
        props.listing.listing_slug,
        props.listing.primary_event_id
      );
      if (!loadListingResult) {
        Toast.error(t('org_listing__an_error_occurred'));
      }
      setLoadingListingId(null);
      reportOpenListing();
    }
  }

  const getDisplayAges = listing => {
    return `${listing?.display_ages} ${t('org_listing__event')}`;
  };

  const renderButton = listing => {
    const end = serverDateToBrowserDate(listing.server_event_end, serverTimezoneName);
    if (!isLaterThanNow(end)) {
      return <></>;
    }

    if (isNotOnSale(listing)) {
      let tSlug = 'org_listing__unavailable';
      if (listing.onsale_status === EVENT_SALE_PAST) {
        tSlug = 'org_listing__sales_ended';
      } else if (listing.onsale_status === EVENT_SALE_FUTURE) {
        tSlug = 'org_listing__not_on_sale';
      } else if (listing.sold_out) {
        tSlug = 'org_listing__sold_out'
      }

      return (
        <Button id={`listing-item__button-${props.listing.listing_id}`} className="full-width button--default" variant={Button.variant.default} disabled>
          <span className="sr-only">{props.listing.listing_title} is </span>
          {t(tSlug)}
        </Button>
      );
    }

    if (embeddedCheckout) {
      return (
        <Button
          id={`listing-item__button-${props.listing.listing_id}`}
          className="full-width text-decoration-none"
          variant={Button.variant.primary}
          onClick={loadDynamicListing}
          disabled={loadingListingId === listing.listing_id || embedCaughtRenderingError}
        >
          <span className="sr-only">{props.listing.listing_title} is </span>
          {t('org_listing__get_tickets')}
        </Button>
      );
    }

    return (
      <a
        className="button button--primary full-width text-decoration-none"
        href={getBuyTicketsLink(props.listing)}
        target="_blank"
        disabled={embedCaughtRenderingError}
        onClick={reportOpenListing}
      >
        {t('org_listing__get_tickets')}{' '}
        <span className="sr-only">for {props.listing.listing_title}</span>
      </a>
    );
  };

  return (
    <article className="listing-item">
      <div className="listing-item__hero">{getImage(props.listing)}</div>

      <div className="listing-item__content">
        <div>
          <header className="listing-item__header">
            {props.decodeTitle && <h3 className='text-lg-bold' dangerouslySetInnerHTML={{ __html: props.listing.listing_title }} />}
            {!props.decodeTitle && <h3 className='text-lg-bold'>{props.listing.listing_title}</h3>}
          </header>

          <div className="listing-item__description">
            {props.listing?.show_date_and_time && (
              <p
                className={(parseInt(props.listing.children_count) > 0 && !props.listing.single_event_series) || props.listing.multipleTimes ? 'listing-item__description--centered' : ''}
              >
                <i className='icon-calendar' />
                <span className='text-md-medium'>{getDate(props.listing)}</span>
              </p>
            )}
            {props.listing?.show_venue && (props?.listing?.venue_name || props?.listing?.venue_city) && (<p>
              <i className='icon-room' />
              <span className='text-md-medium'>{getVenueInfo(props.listing)}</span>
            </p>)}
            {props?.listing?.display_ages && (<p>
              <i className='icon-user' />
              <span className='text-md-medium'>{getDisplayAges(props.listing)}</span>
            </p>)}
          </div>
        </div>

        <footer className="listing-item__buttons">
          {renderButton(props.listing)}
          {getFooterContent(props.listing)}
        </footer>
      </div>
    </article>
  );
};

ListingListItem.propTypes = {
  listing: PropTypes.object,
  decodeTitle: PropTypes.bool
};

export const ListingListItemSkeleton = () => {
  return (
    <div className="listing-item">
      <div className="listing-item__hero">
        <div className="listing-item__image listing-item__image--empty">
          <i className="icon-calendar" aria-hidden="true" />
        </div>
      </div>

      <div className="listing-item__content listing-item__content--loading">
        <div>
          <div className="listing-item__header">
            <h3>&nbsp;</h3>
          </div>

          <div className="listing-item__description">
            <p>&nbsp;</p>
            <p>&nbsp;</p>
            <p>&nbsp;</p>
          </div>
        </div>

        <div className="listing-item__buttons">
          <Button className="full-width" onClick={() => {/*noop*/}}>&nbsp;</Button>
        </div>
      </div>
    </div>
  );
};

export default ListingListItem;
